/* screens/sales/publicPlans.js — чистая логика вкладки «Публичные» раздела «Тарифы».

   Всё, что может соврать молча, — здесь, отдельно от вёрстки, и каждая функция
   закрыта юнит-тестом (publicPlans.test.js):
   - окно продаж: хранится в UTC, задаётся и читается по МОСКВЕ, конец не включительный;
   - подтверждение перед сохранением называет честные числа (закрепление цены — числом
     сервера, лимиты), а порядок «свежая карточка → диалог → запрос» держит saveFlow;
   - тело PATCH — только изменённые и никогда замороженные поля;
   - тело POST — ровно ключи контракта для каждого режима (term / new / copy).

   Денег здесь не СЧИТАЕМ: цена за месяц и скидка в списке приходят с бэкенда, фронт
   их только печатает. Единственный расчёт — подсказка цены по скидке в редакторе и
   фактическая скидка поправленной цены (planEditor.js) — повторяет формулу сервера
   целыми копейками, а в запрос по-прежнему уходит только `price_rub`. */

import { fmt } from '../../lib/format.js';
import {
  FEATURE_LABEL, SLUG_RE, PERIOD_FIELD, LIMIT_FIELDS, num, seatsText, featureObj, sortedJson,
  featureRowsOf, validateName, validatePrice, validateInts, validateNote,
} from './planForm.jsx';
import {
  discountBaseOf, discountError, discountShown, initialDiscount, periodCustomOf, periodMonths, termMonths,
} from './planEditor.js';

// Неразрывный пробел — кодом: невидимый символ в литерале теряется при первой правке.
export const NBSP = String.fromCharCode(0xA0);


/* ── Москва ↔ UTC ──
   Окно продаж задают в поясе платформы (старт акции — 00:00 МСК), а хранит сервер
   в UTC. Поле ввода — `datetime-local` без пояса, поэтому пересчёт явный, через
   Intl с `timeZone`, а не через пояс браузера: у менеджера во Владивостоке
   «19.10 00:00» иначе уехало бы на семь часов раньше. Смещение берём у Intl, а
   не константой +3: правила пояса меняет закон, а не наш код. */
const MSK = 'Europe/Moscow';

function mskParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: MSK, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return parts;
}

const toDate = (v) => {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d) ? null : d;
};

// Смещение Москвы от UTC в момент `ms` (мс): московские «часы на стене» как UTC минус сам момент.
function mskOffsetMs(ms) {
  const p = mskParts(new Date(ms));
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return wall - Math.floor(ms / 1000) * 1000;
}

export function isoToMskInput(iso) {
  const d = toDate(iso);
  if (!d) return '';
  const p = mskParts(d);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

const INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

// 'ГГГГ-ММ-ДДTЧЧ:мм' по Москве → ISO в UTC; мусор и несуществующая дата — null
// (а не «сейчас»: молча подставленная дата открыла бы продажи не в тот день).
export function mskInputToIso(value) {
  const m = INPUT_RE.exec(String(value == null ? '' : value).trim());
  if (!m) return null;
  const [y, mo, d, h, mi, s] = [+m[1], +m[2], +m[3], +m[4], +m[5], +(m[6] || 0)];
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let utc = wall - mskOffsetMs(wall);
  const again = wall - mskOffsetMs(utc);
  if (again !== utc) utc = again;
  // Обратная проверка ловит «31 февраля»: Date.UTC молча перенёс бы его на март.
  const back = mskParts(new Date(utc));
  if (+back.year !== y || +back.month !== mo || +back.day !== d || +back.hour !== h || +back.minute !== mi) return null;
  return new Date(utc).toISOString();
}

/* Метка момента по Москве: «19.10.2026» в полночь, иначе «19.10.2026 12:00».
   `exclusiveEnd` — для конца окна: граница на сервере НЕ включительна (00:00
   следующего дня, так же её читают BuyPlan и выбор тарифа у UTM-меток), поэтому
   полночная граница печатается последним днём продаж. Без этого «по 22.10»
   обещало бы клиенту лишний день акции. */
export function mskStamp(value, { exclusiveEnd = false } = {}) {
  const d = toDate(value);
  if (!d) return '';
  const p = mskParts(d);
  const midnight = p.hour === '00' && p.minute === '00' && p.second === '00';
  if (midnight) {
    const q = exclusiveEnd ? mskParts(new Date(d.getTime() - 1)) : p;
    return `${q.day}.${q.month}.${q.year}`;
  }
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}

// «продажи с … по …» — одна формулировка для строки дерева и превью в редакторе.
export function windowLabel(startsAt, endsAt) {
  const start = toDate(startsAt);
  const end = toDate(endsAt);
  if (!start && !end) return '';
  return ['продажи',
    start ? `с ${mskStamp(start)}` : '',
    end ? `по ${mskStamp(end, { exclusiveEnd: true })}` : '',
  ].filter(Boolean).join(' ');
}

/* ── Статус продажи ──
   «снят с продажи» — выключенный тариф (окно не важно); «продаётся» — без окна;
   «продажи с … по …» — с окном. Предстоящее, идущее и закрытое окно различаются
   тоном и пометкой. Идёт ли продажа сейчас — слово СЕРВЕРА (`on_sale`, его часы):
   часы браузера спешат или отстают, и на границе окна статус соврал бы. К `now`
   обращаемся только когда сервер поля не прислал, и чтобы отличить «ещё не
   началось» от «уже кончилось». */
export function saleStatus(plan, now = new Date()) {
  const p = plan || {};
  if (!p.is_active) return { key: 'off', label: 'снят с продажи', tone: 'neutral', note: '' };
  const start = toDate(p.sale_starts_at);
  const end = toDate(p.sale_ends_at);
  if (!start && !end) {
    return p.on_sale === false
      ? { key: 'off', label: 'не продаётся', tone: 'neutral', note: '' }
      : { key: 'on', label: 'продаётся', tone: 'ok', note: '' };
  }
  const label = windowLabel(start, end);
  const t = (now instanceof Date ? now : new Date(now)).getTime();
  const onSale = typeof p.on_sale === 'boolean'
    ? p.on_sale
    : (!start || start.getTime() <= t) && (!end || t < end.getTime());
  if (onSale) return { key: 'window', label, tone: 'ok', note: '' };
  if (end && end.getTime() <= t) return { key: 'ended', label, tone: 'neutral', note: 'окно закрыто' };
  return { key: 'upcoming', label, tone: 'info', note: 'ещё не начались' };
}

/* ── Подписи строки ── */

// Срок — месяцами, когда он месячный (30·N дней, допуск — на исторические 365),
// иначе днями: «0 мес.» у семидневного демо читалось бы как «бессрочно».
export function termText(plan) {
  const p = plan || {};
  const days = p.period_days;
  const m = p.months;
  if (m >= 1 && days && Math.abs(days - m * 30) <= 5) return `${m}${NBSP}мес.`;
  if (days) return `${days}${NBSP}${fmt.plural(days, 'день', 'дня', 'дней')}`;
  return '—';
}

// Запросы в строке дерева идут после слова «запросы» — строчными, как голос.
export function chatText(v) {
  if (v == null || isNaN(v)) return '—';
  if (v === -1) return 'безлимит';
  if (v === 0) return 'нет';
  return Number(v).toLocaleString('ru-RU');
}

// Голос хранится в секундах; менеджер думает минутами.
export function voiceText(sec) {
  if (sec == null || isNaN(sec)) return '—';
  if (sec === -1) return 'безлимит';
  if (sec === 0) return 'нет';
  if (sec % 60 === 0) return `${(sec / 60).toLocaleString('ru-RU')}${NBSP}мин`;
  return `${Number(sec).toLocaleString('ru-RU')}${NBSP}сек`;
}

export const seatsNb = (n) => `${n}${NBSP}${fmt.plural(n, 'пространство', 'пространства', 'пространств')}`;
const subsText = (n) => `${n} ${fmt.plural(n, 'подписка', 'подписки', 'подписок')}`;

/* ── Заморозка ──
   Срок, автопродление, аудитория и «после текущего» у тарифа с подписчиками или
   очередью заморожены (D2): их смысл для идущей подписки не определён. Список —
   с сервера (`frozen_fields`), сами не выводим. У нового тарифа и копии
   подписчиков нет — замораживать нечего. */
export const FREEZABLE = ['period_days', 'renewable', 'audience', 'starts_after_current'];

export function fieldLocks(plan, mode) {
  if (mode !== 'edit' || !plan) return {};
  const out = {};
  (plan.frozen_fields || []).forEach((f) => { if (FREEZABLE.includes(f)) out[f] = true; });
  return out;
}

/* Почему поля заморожены — перечисляем ВСЕ причины сервера: подписки, очередь и
   неоплаченные деньги (счета/платежи в ожидании и живые ссылки менеджера). Без
   последних подсказка писала бы «на тарифе 0 пространств», а поле всё равно было
   заблокировано — сервер морозит, пока по тарифу может прийти оплата. */
export function frozenHint(plan) {
  const p = plan || {};
  if (!(p.frozen_fields || []).length) return '';
  const ws = p.workspaces_count || 0;
  const queued = p.queued_count || 0;
  const payments = p.pending_payments_count || 0;
  const links = p.live_links_count || 0;
  const parts = [];
  if (ws > 0) parts.push(`на тарифе ${seatsText(ws)}`);
  if (queued > 0) parts.push(`${queued} в очереди на него`);
  if (payments > 0) parts.push(`неоплаченных счетов и платежей: ${payments}`);
  if (links > 0) parts.push(`живых ссылок на оплату: ${links}`);
  const tail = ' — чтобы изменить, создайте копию.';
  if (!parts.length) return `На тарифе есть подписки или ожидаемые оплаты${tail}`;
  const text = parts.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1) + tail;
}

/* ── Подтверждение перед сохранением ──
   Цена. Сервер закрепляет прежнюю цену у ПЛАТНОГО продлеваемого тарифа за ВСЕМИ, кто
   на нём сидит и ещё без своей закреплённой цены, — с автопродлением или без (его могут
   включить обратно), активными или нет (A1, шрам S41). Сколько их — говорит сам сервер
   (`pin_on_change_count`, тот же предикат, что у закрепления), и число печатается как
   есть, без своей арифметики. Подписки в ОЧЕРЕДИ на тариф держат цену, с которой встали
   в очередь (`pending_plan_price_rub`), — о них так и пишем. «Остальных, кто заплатит
   новую цену» среди сидящих на тарифе нет: новую цену платит только новая покупка.
   Лимиты и фичи: применяются сразу ко всем на тарифе. Числа — из свежей карточки
   тарифа (её читает saveFlow перед диалогом), а не из списка часовой давности.
   Первая строка при смене цены — «Цена: было X → станет Y» (верификатор, раунд 2):
   без неё «Сменить цену?» не говорил, НА ЧТО меняем, и цену, пересчитанную от скидки
   незаметно для менеджера, было не поймать в самом диалоге. «Было» — из свежей
   карточки (цену мог поменять коллега, пока экран был открыт), «станет» — из формы. */
export const LIMIT_KEYS = [...LIMIT_FIELDS.map((f) => f.key), 'features'];

const moneyOf = (v) => fmt.money(v === '' || v == null ? NaN : Number(String(v).trim()));
const priceLine = (p, form) => `Цена: было ${moneyOf(p.price_rub)} → станет ${moneyOf(form && form.price_rub)}`;

// Прежняя цена 0 — закреплять нечего (сервер и не закрепляет): «сохранят прежнюю
// цену 0 ₽» обещало бы бесплатное продление тем, кто его и так не оплачивал.
const isFreePlan = (p) => !p.is_paid || !(Number(p.price_rub) > 0);

function priceChangeLines(p) {
  if (isFreePlan(p)) return ['Закреплять нечего: тариф бесплатный — новая цена для всех будущих оплат.'];
  // Разовый тариф не продлевается сам — закреплять нечего, и слов о закреплении
  // здесь быть не должно: каждая следующая покупка идёт по цене на её момент.
  if (!p.renewable) return ['Новая цена — для новых покупок и всех будущих оплат: тариф без автопродления.'];

  const lines = [];
  const pinNow = p.pin_on_change_count || 0;
  const pinned = p.pinned_count || 0;
  const queued = p.queued_count || 0;
  const head = 'Новая цена — для новых покупок.';
  if (pinNow > 0) {
    lines.push(`${head} ${subsText(pinNow)} ${fmt.plural(pinNow, 'сохранит', 'сохранят', 'сохранят')} прежнюю цену продления ${fmt.money(p.price_rub)}.`);
  } else {
    lines.push(`${head} Закреплять прежнюю цену некому: на тарифе нет подписок без закреплённой цены.`);
  }
  if (pinned > 0) {
    lines.push(`${pinNow > 0 ? 'Ещё ' : ''}${subsText(pinned)} уже ${fmt.plural(pinned, 'продлевается', 'продлеваются', 'продлеваются')} по закреплённой цене — она не изменится.`);
  }
  // Очередь держит цену, с которой встала в очередь (снимок pending_plan_price_rub
  // превращается в закреплённую цену при переключении) — проверено живыми оплатами.
  if (queued > 0) {
    lines.push(`${subsText(queued)} в очереди на тариф — ${fmt.plural(queued, 'её', 'их', 'их')} продления пойдут по цене, с которой ${fmt.plural(queued, 'она встала', 'они встали', 'они встали')} в очередь.`);
  }
  return lines;
}

// Подсказка поля «Цена» в редакторе — та же развилка, что у подтверждения: обещать
// «закрепится при сохранении» у бесплатного или разового тарифа было бы неправдой.
export function priceHint(plan) {
  const p = plan || {};
  if (isFreePlan(p)) return 'Тариф бесплатный: закреплять нечего — новая цена для всех будущих оплат.';
  if (!p.renewable) return 'Новая цена — для новых покупок и всех будущих оплат: тариф без автопродления.';
  return 'Новая цена — для новых покупок. Все, кто уже на тарифе, продолжат продлеваться по прежней цене — сколько их, покажем перед сохранением.';
}

export function saveConfirm(plan, keys, form) {
  const p = plan || {};
  const changed = new Set(keys || []);
  const lines = [];
  const priceChanged = changed.has('price_rub');
  const ws = p.workspaces_count || 0;
  const limitsChanged = LIMIT_KEYS.some((k) => changed.has(k)) && ws > 0;

  if (priceChanged) lines.push(priceLine(p, form), ...priceChangeLines(p));
  if (limitsChanged) {
    lines.push(`Изменится сразу у ${ws} ${fmt.plural(ws, 'пространства', 'пространств', 'пространств')}: лимиты и фичи тарифа действуют для всех, кто на нём сидит.`);
  }
  if (!lines.length) return null;
  const title = priceChanged && limitsChanged ? 'Сохранить изменения?' : priceChanged ? 'Сменить цену?' : 'Изменить лимиты?';
  return { title, lines };
}

// Тост после сохранения — о том, что СДЕЛАЛ сервер (pinned_now / affected_now),
// а не о том, что мы ожидали. affected_now сервер шлёт всегда (= число
// пространств), поэтому про лимиты говорим, только если их правили.
export function savedToastText(effects, keys) {
  const e = effects || {};
  const changed = new Set(keys || []);
  const parts = ['Тариф сохранён.'];
  const pinned = e.pinned_now > 0 ? e.pinned_now : 0;
  const affected = e.affected_now > 0 ? e.affected_now : 0;
  if (pinned) {
    parts.push(`Прежняя цена закреплена за ${pinned} ${fmt.plural(pinned, 'подпиской', 'подписками', 'подписками')}.`);
  }
  if (affected && LIMIT_KEYS.some((k) => changed.has(k))) {
    parts.push(`Лимиты и фичи обновлены у ${affected} ${fmt.plural(affected, 'пространства', 'пространств', 'пространств')}.`);
  }
  return parts.join(' ');
}

/* ── Форма ──
   Один набор полей на все режимы; значения — как их держат инпуты (цена и целые —
   строкой или числом, окно — строкой поля ввода по Москве).
   Поля редактора, которых нет в запросе (planEditor.js): period_custom — выбран срок
   «Свой», period_custom_days — его дни (свои у тарифа или набранные: к ним вернётся
   «Свой» после «N мес.»); discount_pct / discount_typed / price_before_discount — поле
   скидки к помесячной цене; у «Нового срока» — months_custom. В PATCH и POST их не
   берёт ни один построитель тела.
   `base` — основа скидки (discountBaseOf семейства): у срока семейства поле скидки
   открывается уже с фактической скидкой текущей цены — по месяцам сервера
   (periodMonths), и у «Своего» срока тоже. */
const NEW_DEFAULTS = {
  price_rub: '', period_days: 30, period_custom: false, period_custom_days: '', renewable: true, audience: 'personal',
  starts_after_current: false,
  chat_messages_per_30d: 300, voice_seconds_per_30d: 4320, max_documents: 50,
  max_storage_mb: 1024, max_file_size_mb: 25,
  show_in_storefront: true, is_active: false, sale_starts_at: '', sale_ends_at: '', sales_note: '',
  discount_pct: '', discount_typed: false,
};

export function toPublicForm(mode, { plan, source, baseKey, catalog, base } = {}) {
  if (mode === 'term') {
    return { base_slug: baseKey || '', months: '', months_custom: false, price_rub: '', discount_pct: '', discount_typed: false };
  }
  if (mode === 'copy') {
    const s = source || {};
    return { from_slug: s.slug || '', slug: '', name: s.name || '' };
  }
  if (mode === 'new') {
    // Новый тариф — фичи каталога выключены: включают явно, глядя на подписи,
    // а не получают молча «как у сетевого клиента».
    const keys = (catalog && catalog.length ? catalog.map((c) => c.key) : Object.keys(FEATURE_LABEL));
    return { ...NEW_DEFAULTS, slug: '', name: '', features: keys.map((key) => ({ key, on: false })) };
  }
  const p = plan || {};
  const price = p.price_rub != null ? String(Number(p.price_rub)) : '';
  const periodDays = p.period_days ?? 30;
  const periodCustom = periodCustomOf(periodDays);
  return {
    slug: p.slug || '',
    name: p.name || '',
    price_rub: price,
    period_days: periodDays,
    period_custom: periodCustom,
    // Свои дни тарифа вне «N мес.» (365, 45): без них «Свой» после «12 мес.» показал бы
    // 360, и сохранение молча укоротило бы тариф.
    period_custom_days: periodCustom ? periodDays : '',
    discount_pct: initialDiscount(price, periodMonths({ period_days: periodDays }), base),
    discount_typed: false,
    renewable: !!p.renewable,
    audience: p.audience || 'personal',
    starts_after_current: !!p.starts_after_current,
    chat_messages_per_30d: p.chat_messages_per_30d ?? 0,
    voice_seconds_per_30d: p.voice_seconds_per_30d ?? 0,
    max_documents: p.max_documents ?? 0,
    max_storage_mb: p.max_storage_mb ?? 0,
    max_file_size_mb: p.max_file_size_mb ?? 1,
    // Только флаги и ровно те, что в тарифе есть: дописать «известные» с false
    // значило бы записать в features то, чего там не было.
    features: featureRowsOf(p.features),
    show_in_storefront: p.show_in_storefront !== false,
    is_active: !!p.is_active,
    sale_starts_at: isoToMskInput(p.sale_starts_at),
    sale_ends_at: isoToMskInput(p.sale_ends_at),
    sales_note: p.sales_note || '',
  };
}

const INT_KEYS = [PERIOD_FIELD.key, ...LIMIT_FIELDS.map((f) => f.key)];
const BOOL_KEYS = ['renewable', 'starts_after_current', 'show_in_storefront', 'is_active'];
const TEXT_KEYS = ['name', 'sales_note'];
const DATE_KEYS = ['sale_starts_at', 'sale_ends_at'];
// Что вообще бывает в PATCH (контракт: editable_always + editable_with_effects +
// frozen_when_subscribed). Слага, is_private, is_default здесь нет и быть не может.
const PATCH_KEYS = ['name', 'price_rub', ...INT_KEYS, 'renewable', 'audience', 'starts_after_current',
  'features', 'show_in_storefront', 'sale_starts_at', 'sale_ends_at', 'is_active', 'sales_note'];

function fieldValue(form, key) {
  if (TEXT_KEYS.includes(key)) return String(form[key] || '').trim();
  if (key === 'price_rub') return String(form.price_rub == null ? '' : form.price_rub).trim();
  if (INT_KEYS.includes(key)) return Number(form[key]);
  if (key === 'features') return featureObj(form.features || []);
  if (DATE_KEYS.includes(key)) return form[key] ? mskInputToIso(form[key]) : null;
  if (key === 'audience') return form.audience;
  return !!form[key];
}

function isChanged(initial, form, key) {
  if (TEXT_KEYS.includes(key)) return String(form[key] || '').trim() !== String(initial[key] || '').trim();
  if (key === 'price_rub' || INT_KEYS.includes(key)) return num(form[key]) !== num(initial[key]);
  if (key === 'features') return sortedJson(featureObj(form.features || [])) !== sortedJson(featureObj(initial.features || []));
  if (DATE_KEYS.includes(key)) return String(form[key] || '') !== String(initial[key] || '');
  if (key === 'audience') return form.audience !== initial.audience;
  if (BOOL_KEYS.includes(key)) return !!form[key] !== !!initial[key];
  return false;
}

// Изменённые поля, которые сервер вообще примет: замороженные — никогда (сервер
// отбил бы ВСЮ правку как frozen_field, а инпуты их и так не дают трогать).
export function changedKeys(initial, form, plan) {
  const locks = fieldLocks(plan, 'edit');
  return PATCH_KEYS.filter((k) => !locks[k] && isChanged(initial, form, k));
}

// PATCH обязан быть частичным: правка одной цены не должна переписывать поля,
// которых менеджер не касался (и уж тем более — замороженные).
export function buildPatchBody(initial, form, plan) {
  const body = {};
  changedKeys(initial, form, plan).forEach((k) => { body[k] = fieldValue(form, k); });
  return body;
}

// Слаг НЕ приводим к нижнему регистру: сервер заглавные отвергает (D9a) — молча
// исправлять за менеджера значило бы создать тариф не с тем адресом, что он ввёл
// (живой QA 17.10: «Expert_Nov» уходил как «expert_nov»). Только обрезаем пробелы.
export const normSlug = (s) => String(s || '').trim();
export const termSlug = (base, months) => `${base}_${Number(months)}m`;
// Превью того, что соберёт сервер: '<Название базы> (N мес.)'.
export const termName = (baseName, months) => `${baseName} (${Number(months)} мес.)`;

// is_active не отправляется ни в одном режиме: созданный тариф сервер ВСЕГДА
// ставит снятым с продажи (D3), а поле в теле обещало бы обратное.
export function buildCreateBody(mode, form) {
  if (mode === 'term') {
    return { mode: 'term', base_slug: form.base_slug, months: Number(form.months), price_rub: fieldValue(form, 'price_rub') };
  }
  if (mode === 'copy') {
    const body = { mode: 'copy', from_slug: form.from_slug, slug: normSlug(form.slug) };
    const name = String(form.name || '').trim();
    if (name) body.name = name;
    return body;
  }
  const body = { mode: 'new', slug: normSlug(form.slug) };
  ['name', 'audience', 'price_rub', ...INT_KEYS, 'renewable', 'features', 'starts_after_current',
    'sale_starts_at', 'sale_ends_at', 'show_in_storefront', 'sales_note']
    .forEach((k) => { body[k] = fieldValue(form, k); });
  return body;
}

/* ── Проверка формы до запроса ──
   ctx.existingSlugs — слаги ВСЕХ публичных тарифов из дерева (включая снятые с
   продажи). Последнее слово за сервером (409/400), здесь — очевидное. */
// Тот же разбор, что у сервера (plan_catalog._TERM_RE): жадная база, «_N m» в конце.
const TERM_RE = /^(.+)_(\d+)m$/;

function slugError(raw, ctx) {
  const slug = normSlug(raw);
  if (/[A-ZА-ЯЁ]/.test(slug)) return 'Только строчные буквы: слаг становится адресом ссылки, заглавные сервер не примет.';
  if (!SLUG_RE.test(slug)) return 'Латиница в нижнем регистре, цифры, «-» и «_», 2–64 символа.';
  if (ctx.existingSlugs && ctx.existingSlugs.has(slug)) return 'Тариф с таким слагом уже есть.';
  const m = TERM_RE.exec(slug);
  // `<x>_<N>m`, где `<x>` — ЛЮБОЙ существующий публичный слаг (не только база
  // семейства), сервер отбивает: дерево и витрина прочли бы его как срок тарифа `<x>`.
  // Проверять только базы семейств значило бы пропустить, например, `expert_6m_3m`
  // и `expert_1eq2_3m` — и узнать об отказе лишь после раунд-трипа.
  if (m && ctx.existingSlugs && ctx.existingSlugs.has(m[1])) {
    return `Слаг «${slug}» читается как срок тарифа «${m[1]}» — выберите другой. Срок существующего тарифа заводят кнопкой «Новый срок».`;
  }
  // Любой `…_<N>m` сервер в «новом» и «копии» отбивает: витрина клиента прячет такой
  // слаг как срок, а заведённая позже база `<x>` молча забрала бы его в своё семейство.
  if (m) return `Окончание «_${m[2]}m» зарезервировано за сроками тарифов — выберите другой слаг.`;
  return '';
}

/* Скидка к помесячной: от какой основы и за сколько месяцев. Одно правило на дровер
   (видно ли поле, от чего считать) и на проверку формы: разойдись они — проверка
   закрывала бы сохранение ошибкой поля, которого на экране нет. Срок семейства — от
   его основы (expert_6m → expert), «Новый срок» — от выбранного тарифа, у нового
   тарифа и копии семейства нет. Месяцы — правилом сервера (periodMonths / termMonths). */
export function discountContext(mode, form, plan, ctx) {
  const c = ctx || {};
  const f = form || {};
  let base = null;
  if (mode === 'term') base = discountBaseOf(c.bySlug ? c.bySlug.get(f.base_slug) : null);
  else if (mode === 'edit' && plan) base = discountBaseOf(c.baseOf ? c.baseOf.get(plan.slug) : null);
  const months = mode === 'term' ? termMonths(f) : periodMonths(f);
  return { base, months, shown: discountShown(base, months) };
}

// `plan` — правимый тариф (у правки скидка считается от основы ЕГО семейства).
export function validatePublicForm(form, mode, ctx = {}, plan = null) {
  const e = {};
  const put = (k, msg) => { if (msg) e[k] = msg; };
  // Неверная набранная скидка (150, 5.5, «-») закрывает сохранение: цена под полем
  // осталась от прежнего нажатия и выглядела бы ценой по скидке. Только когда поле
  // видно — скрытое поле менеджер исправить не может.
  const discount = () => {
    if (discountContext(mode, form, plan, ctx).shown) put('discount_pct', discountError(form));
  };

  if (mode === 'term') {
    if (!form.base_slug) e.base_slug = 'Выберите тариф, к которому добавляется срок.';
    const m = num(form.months);
    if (!Number.isInteger(m) || m < 2 || m > 36) e.months = 'Целое число месяцев от 2 до 36.';
    else if (form.base_slug && ctx.existingSlugs && ctx.existingSlugs.has(termSlug(form.base_slug, m))) {
      e.months = `Срок ${m} мес. у этого тарифа уже есть — правьте его в списке.`;
    }
    put('price_rub', validatePrice(form.price_rub));
    discount();
    return e;
  }

  if (mode === 'copy') {
    put('slug', slugError(form.slug, ctx));
    put('name', validateName(form.name, { required: false }));
    return e;
  }

  if (mode === 'new') put('slug', slugError(form.slug, ctx));
  put('name', validateName(form.name));
  put('price_rub', validatePrice(form.price_rub));
  discount();
  Object.assign(e, validateInts(form, [PERIOD_FIELD, ...LIMIT_FIELDS]));
  put('sales_note', validateNote(form.sales_note));

  const start = form.sale_starts_at ? mskInputToIso(form.sale_starts_at) : null;
  const end = form.sale_ends_at ? mskInputToIso(form.sale_ends_at) : null;
  if (form.sale_starts_at && !start) e.sale_starts_at = 'Дата и время в формате ДД.ММ.ГГГГ ЧЧ:ММ.';
  if (form.sale_ends_at && !end) e.sale_ends_at = 'Дата и время в формате ДД.ММ.ГГГГ ЧЧ:ММ.';
  if (start && end && new Date(end) <= new Date(start)) e.sale_ends_at = 'Окончание продаж должно быть позже начала.';
  return e;
}

/* ── Ошибки сервера → поля формы ──
   detail с бэка написан по-русски и точен — показываем его как есть; фолбэк —
   на случай ответа без тела. Коды контракта раскладываем по полям, чтобы
   менеджер видел, ЧТО именно не принято. */
const ERROR_FALLBACK = {
  frozen_field: 'Эти поля заморожены: на тарифе есть подписки — создайте копию.',
  slug_taken: 'Тариф с таким слагом уже есть.',
  default_plan_locked: 'Тариф по умолчанию нельзя снять с продажи.',
  invalid_payload: 'Сервер не принял данные тарифа — проверьте поля.',
  unknown_base: 'Тариф-основа не найден среди публичных.',
  not_found: 'Тариф не найден — возможно, его уже нет среди публичных.',
};
const FROZEN_FIELD_ERROR = 'Заморожено: на тарифе есть подписки — создайте копию.';

// Слаг в создании: в «новом» и «копии» его вводят руками, у «нового срока» его
// собирает сервер из базы и месяцев — там виноватое поле — месяцы.
const SLUG_FIELD_BY_MODE = { new: 'slug', copy: 'slug', term: 'months' };
const MENTIONS_SLUG_RE = /слаг|slug/i;

export function serverErrorOf(res, { mode } = {}) {
  const d = res && res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : {};
  const status = res ? res.status : 0;
  const code = typeof d.code === 'string' ? d.code : '';
  let detail = typeof d.detail === 'string' && d.detail ? d.detail : (ERROR_FALLBACK[code] || '');
  if (!detail) {
    detail = status === 403 ? 'Нет прав настраивать публичные тарифы.'
      : status === 0 ? 'Сервер не ответил — проверьте соединение, обновите список и попробуйте ещё раз.'
        : 'Не удалось сохранить тариф.';
  }
  const fields = {};
  // DRF-словарь {поле: [сообщения]} — на случай валидатора сериализатора.
  Object.entries(d).forEach(([k, v]) => {
    if (k === 'fields' || k === 'detail' || k === 'code') return;
    if (Array.isArray(v) && v.length && v.every((x) => typeof x === 'string')) fields[k] = v.join(' ');
  });
  if (code === 'frozen_field' && Array.isArray(d.fields)) {
    d.fields.forEach((f) => { if (typeof f === 'string') fields[f] = FROZEN_FIELD_ERROR; });
  }
  const slugField = SLUG_FIELD_BY_MODE[mode] || 'slug';
  if (code === 'slug_taken') fields[slugField] = detail;
  // Правило слага (алфавит, «читается как срок») сервер отдаёт как invalid_payload:
  // без раскладки по полю менеджер видел бы ошибку над формой, а поле — чистым.
  if (code === 'invalid_payload' && SLUG_FIELD_BY_MODE[mode] && MENTIONS_SLUG_RE.test(String(d.detail || ''))) {
    fields[slugField] = detail;
  }
  if (code === 'default_plan_locked') fields.is_active = detail;
  if (code === 'unknown_base') fields.base_slug = detail;
  return { code, detail, fields };
}

/* ── Сохранение: проверка → (свежая карточка → диалог) → запрос ──
   Вынесено из дровера чистой функцией, чтобы порядок шагов был проверяем без
   браузера: числа диалога — из карточки, прочитанной ПОСЛЕ нажатия «Сохранить»
   и ДО диалога; отказ в диалоге — ни одного PATCH; тело PATCH — ровно
   buildPatchBody; тело POST — buildCreateBody (без is_active).
   `api` — модуль salesConsole (запросы + нормализаторы), `confirm(ask)` — диалог,
   отвечает true/false; `busy(bool)` — индикатор кнопки (на время диалога гаснет).
   Итог — {status:'invalid'|'noop'|'cancelled'|'error'|'saved', …}. */
export async function saveFlow({ mode, form, initial, plan, ctx, api, confirm, busy = () => {} }) {
  const errors = validatePublicForm(form, mode, ctx || {}, plan);
  if (Object.keys(errors).some((k) => errors[k])) return { status: 'invalid', errors };

  try {
    if (mode === 'edit') {
      const keys = changedKeys(initial, form, plan);
      if (keys.length === 0) return { status: 'noop' };
      busy(true);
      if (keys.includes('price_rub') || LIMIT_KEYS.some((k) => keys.includes(k))) {
        // «7 подписок сохранят прежнюю цену» обязано быть правдой на момент нажатия:
        // список мог устареть, пока экран был открыт.
        const fresh = await api.getPublicPlan(plan.slug);
        const freshPlan = fresh && fresh.ok ? api.publicPlanOf(fresh.data) : null;
        const ask = saveConfirm(freshPlan || plan, keys, form);
        if (ask) {
          // Сервер не отдал карточку — показываем числа списка, но честно говорим,
          // что они могли устареть, а не выдаём их за свежие.
          if (!freshPlan) ask.lines.push('Свежие числа получить не удалось — показаны данные списка, они могли устареть.');
          busy(false);
          const ok = await confirm(ask);
          if (!ok) return { status: 'cancelled' };
          busy(true);
        }
      }
      const res = await api.patchPublicPlan(plan.slug, buildPatchBody(initial, form, plan));
      if (!res || !res.ok) return { status: 'error', error: serverErrorOf(res, { mode }) };
      return {
        status: 'saved', kind: 'edit', slug: plan.slug,
        message: savedToastText(api.saveEffectsOf(res.data), keys),
      };
    }

    busy(true);
    const res = await api.createPublicPlan(buildCreateBody(mode, form));
    if (!res || !res.ok) return { status: 'error', error: serverErrorOf(res, { mode }) };
    // Слаг — ТОЛЬКО из ответа сервера: открыть карточку по слагу, который мы
    // предполагали, значило бы открыть не тот тариф (или ничего), если сервер его
    // собрал иначе. Нет слага в ответе — карточку не открываем, не угадываем.
    const created = api.publicPlanOf(res.data);
    const slug = created ? created.slug : '';
    const name = (created && created.name) || slug;
    return {
      status: 'saved', kind: 'create', slug,
      message: name
        ? `Тариф «${name}» создан снятым с продажи. Проверьте условия и включите «Продаётся», когда будете готовы.`
        : 'Тариф создан снятым с продажи. Найдите его в списке и включите «Продаётся», когда будете готовы.',
    };
  } catch (e) {
    // apiFetch не ловит отказ самого fetch (нет сети, обрыв): без этого менеджер
    // нажимал «Сохранить», кнопка гасла и снова загоралась — и ни слова (QA 17.10).
    // Состояние на сервере неизвестно, поэтому текст не обещает «не сохранено».
    return { status: 'error', error: serverErrorOf({ status: 0, data: null }, { mode }) };
  } finally {
    busy(false);
  }
}

/* ── Открытая карточка в адресе (?plan=) ──
   После создания открываем карточку нового тарифа (он снят с продажи — включить
   продажу удобнее не выходя), после правки — закрываем. */
export const planParamAfterSave = (saved) => (saved && saved.kind === 'create' && saved.slug ? saved.slug : '');

export function withPlanParam(prev, slug) {
  const sp = new URLSearchParams(prev);
  if (slug) sp.set('plan', slug); else sp.delete('plan');
  return sp;
}

/* ── Справочник по дереву ──
   Слаги всех публичных тарифов (проверка «уже есть»), базы семейств (запрет
   захвата семейства слагом `<база>_<N>m`), тарифы, к которым можно добавить срок
   (платные, не акции — у акции базы-семейства нет), и основа семейства у каждого
   срока (`baseOf`: expert_6m → expert) — от неё редактор считает скидку к
   помесячной цене. Семейства собирает сервер (plan_catalog) — сами по слагу не
   угадываем: акция `expert_2m` с окном продаж — отдельная строка, а не срок Expert.
   Основа берётся у ЛЮБОГО семейства, как у сервера (`plan_tree` считает скидку
   каждого срока от базы, акция она или нет); годится ли она для скидки (платная, на
   месяц), решает discountBaseOf. */
export function treeContext(data) {
  const existingSlugs = new Set();
  const baseKeys = new Set();
  const termBases = [];
  const bySlug = new Map();
  const baseOf = new Map();
  for (const g of (data && data.groups) || []) {
    for (const f of g.families || []) {
      baseKeys.add(f.key);
      for (const p of f.plans || []) { existingSlugs.add(p.slug); bySlug.set(p.slug, p); }
      const base = (f.plans || []).find((p) => p.slug === f.key);
      if (base) {
        for (const p of f.plans || []) if (p.slug !== base.slug) baseOf.set(p.slug, base);
      }
      if (!f.is_promo && base && base.is_paid) {
        termBases.push({ value: f.key, label: f.name, group: g.label, plan: base });
      }
    }
  }
  return { existingSlugs, baseKeys, termBases, bySlug, baseOf };
}
